import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Button,
  Typography,
  Grid
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AlertService from '../Alerts/alerts';

const AddressPickerPopup = ({ open, onClose, onSelect }) => {
  const [formData, setFormData] = useState({
    location: '',
    block: '',
    unit: '',
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [popupKey, setPopupKey] = useState(0); // Force re-render

  // 🔥 Force Reinitialization when the popup opens
  useEffect(() => {
    if (open) {
      setPopupKey((prevKey) => prevKey + 1);
      setTimeout(() => {
        initAutocomplete();
      }, 300);
    }
  }, [open]);

  const initAutocomplete = () => {
    if (!inputRef.current) return;

    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['establishment','geocode'],
      fields: ['address_components', 'geometry', 'formatted_address']
    });

    autocomplete.addListener('place_changed', handlePlaceSelect);
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place.geometry || !place.geometry.location) {
      AlertService.swalErrorAlert('Please select a valid address.');
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const parsedAddress = parseAddressComponents(place.address_components);

    setFormData({
      ...parsedAddress,
      latitude: lat,
      longitude: lng,
      location: place.formatted_address
    });
  };

  const parseAddressComponents = (components) => {
    let block = '', unit = '', city = '', state = '', postalCode = '', country = '';
    components.forEach((component) => {
      const types = component.types;
      if (types.includes('street_number')) unit = component.long_name;
      else if (types.includes('route')) block = component.long_name;
      else if (types.includes('locality')) city = component.long_name;
      else if (types.includes('administrative_area_level_1')) state = component.long_name;
      else if (types.includes('postal_code')) postalCode = component.long_name;
      else if (types.includes('country')) country = component.long_name;
    });
    return { block, unit, city, state, postalCode, country };
  };

  const handleConfirm = () => {
    onSelect(formData);
    onClose();
  };

  return (
    <Dialog key={popupKey} open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Typography variant="h6" gutterBottom>Select Google Address</Typography>
        <TextField
          inputRef={inputRef}
          label="Search"
          fullWidth
          placeholder="Type here..."
          InputProps={{ startAdornment: <LocationOnIcon /> }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mt: 6, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>Confirm</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddressPickerPopup;
